import { useCallback, useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import { decideProtectionUI } from '../types/protection.types';

import type { ProtectionValidation, ProtectionUIDecision } from '../types/protection.types';

/**
 * useProtectionCheck — Sprint Backend Protection v1 Agent C (Item 226).
 *
 * Hook adapter canonical que coleta `validation` retornado pelo `sendOutbound`
 * (success com warn → banner; ApiError 400 com `validation_blocked` → modal) e
 * expõe estado consumível por ProtectionWarningBanner + ProtectionBlockModal.
 *
 * Padrão state local (não react-query / não Zustand) — `validation` é
 * transient: vive apenas até atendente dismissar banner ou fechar/editar o
 * modal informativo. Nada a persistir, nada a sincronizar entre componentes
 * além do Composer pai.
 *
 * F1.3 (bloqueio honesto): o modal de block é apenas informativo — NÃO há
 * override (`acknowledge`). O bloqueio é HARD no Engine.
 *
 * Capability gate canonical: `ai-protection-outbound-validate` (Agent B PR-B).
 * Quando capability ausente, hook retorna estado neutro (`enabled: false`)
 * para que componentes consumidores não renderizem UI alguma.
 *
 * Defesa em profundidade canonical (D-B pattern Brief 5):
 *   - Camada 1 (preventiva): capability gate retorna `enabled: false` cedo.
 *   - Camada 2 (semântica): mesmo se chamado, `setValidation` é no-op quando
 *     capability ausente — garante zero UI surge inadvertidamente.
 */
export interface UseProtectionCheckResult {
  /** Capability `ai-protection-outbound-validate` ativa? */
  enabled: boolean;
  /** Validation atual (warn flow OU block informativo). */
  validation: ProtectionValidation | null;
  /** Decisão UI canonical: 'banner' | 'modal' | 'none'. */
  uiDecision: ProtectionUIDecision;
  /** Define validation (chamado pelo Composer após resposta sendOutbound). */
  setValidation: (v: ProtectionValidation | null) => void;
  /** Limpa estado (dismiss banner ou fechar/editar no modal). */
  clear: () => void;
}

export function useProtectionCheck(): UseProtectionCheckResult {
  const enabled = useCapability('ai-protection-outbound-validate');
  const [validation, setValidationRaw] = useState<ProtectionValidation | null>(null);

  const setValidation = useCallback(
    (v: ProtectionValidation | null) => {
      // Defense-in-depth: capability ausente bloqueia mutação.
      if (!enabled) return;
      setValidationRaw(v);
    },
    [enabled],
  );

  const clear = useCallback(() => {
    setValidationRaw(null);
  }, []);

  const uiDecision = enabled ? decideProtectionUI(validation) : 'none';

  return {
    enabled,
    validation,
    uiDecision,
    setValidation,
    clear,
  };
}
